import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '../lib/api'
import { pagePath } from '../lib/pages'
import { formatAgo, formatDateTime, plural } from '../lib/format'
import type { Customer, Overview } from '../lib/types'
import { StatusBadge } from '../components/Status'
import { ErrorBox, Loading } from '../components/Feedback'

interface Props {
  customerSlug: string | null
}

/** Übersicht nach docs/design/screens/Main.dc.html. */
export function UebersichtPage({ customerSlug }: Props) {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['overview', customerSlug],
    queryFn: ({ signal }) => apiGet<Overview>('/overview', signal),
    refetchInterval: 30_000,
  })
  // Im Untertitel steht der Name des Kunden, nicht sein Kürzel aus der
  // Adresszeile.
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: ({ signal }) => apiGet<Customer[]>('/customers', signal),
    staleTime: 60_000,
    enabled: Boolean(customerSlug),
  })

  if (isError) return <ErrorBox error={error} what="Die Übersicht" />
  if (isPending) return <Loading what="Die Übersicht" />

  const { kpi } = data
  const customerName = customerSlug
    ? (customers.data?.find((c) => c.slug === customerSlug)?.name ?? customerSlug)
    : 'Alle Kunden'
  const visibleAlerts = data.alerts.filter(
    (a) => !a.suppressed_by_alert_id && !a.maintenance_window_id,
  )

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Übersicht</h1>
          <div className="page-sub">
            {customerName} · {plural(kpi.sites, 'Standort', 'Standorte')} ·{' '}
            {plural(kpi.devices, 'Gerät', 'Geräte')}
          </div>
        </div>
      </div>

      <div className="kpi-grid">
        <Kpi label="Geräte" value={kpi.devices} sub={`${kpi.devices_down} nicht erreichbar`}
          tone={kpi.devices_down > 0 ? 'crit' : 'ok'} />
        <Kpi label="Standorte" value={kpi.sites}
          sub={`${kpi.agents_online} von ${plural(kpi.agents_total, 'Agent', 'Agents')} online`}
          tone={kpi.agents_online < kpi.agents_total ? 'warn' : 'ok'} />
        <Kpi label="Offene Alarme" value={kpi.alerts_open} sub={`${kpi.alerts_critical} kritisch`}
          tone={kpi.alerts_critical > 0 ? 'crit' : kpi.alerts_open > 0 ? 'warn' : 'ok'} />
        <Kpi label="Unterdrückt" value={kpi.alerts_suppressed} sub="Folgefehler und Wartung" tone="off" />
        <Kpi label="Agents" value={kpi.agents_online} sub={`von ${kpi.agents_total}`}
          tone={kpi.agents_online < kpi.agents_total ? 'warn' : 'ok'} />
      </div>

      <div className="row" style={{ alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <section className="card" style={{ flexGrow: 1, minWidth: 420 }}>
          <div className="card-head">
            <h2 className="card-title">Aktive Alarme</h2>
            <Link to={pagePath('alarme', customerSlug)} style={{ fontSize: 13 }}>
              Alle Alarme
            </Link>
          </div>
          {visibleAlerts.length === 0 ? (
            <div className="empty">
              <strong>Keine offenen Alarme</strong>
              <span>Alles ruhig.</span>
            </div>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <th>Schwere</th>
                  <th>Alarm</th>
                  <th>Wo</th>
                  <th>Seit</th>
                </tr>
              </thead>
              <tbody>
                {visibleAlerts.slice(0, 8).map((a) => (
                  <tr key={a.id}>
                    <td>
                      <StatusBadge
                        state={a.severity === 'crit' ? 'crit' : 'warn'}
                        label={a.severity === 'crit' ? 'Kritisch' : 'Warnung'}
                      />
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{a.title}</div>
                      <div className="muted" style={{ fontSize: 12 }}>
                        {a.detail}
                      </div>
                    </td>
                    <td className="muted">
                      {a.customer_name}
                      {a.site_name ? ` · ${a.site_name}` : ''}
                    </td>
                    <td className="muted mono">{formatAgo(a.opened_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="card" style={{ width: 380 }}>
          <div className="card-head">
            <h2 className="card-title">Standorte</h2>
            <span className="muted" style={{ fontSize: 12 }}>
              OK · Warn · Down
            </span>
          </div>
          {data.sites.length === 0 ? (
            <div className="empty">
              <span>Noch keine Standorte angelegt.</span>
            </div>
          ) : (
            data.sites.slice(0, 10).map((s) => (
              <div
                key={s.site_id}
                className="row"
                style={{ gap: 12, padding: '12px 18px', borderBottom: '1px solid var(--line)' }}
              >
                <span
                  className="dot"
                  style={{
                    background: !s.agent_online
                      ? 'var(--crit)'
                      : s.devices_down > 0
                        ? 'var(--crit)'
                        : s.devices_warn > 0
                          ? 'var(--warn)'
                          : 'var(--ok)',
                  }}
                />
                <div className="grow" style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{s.site_name}</div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {s.customer_name} · {s.agent_name || 'kein Agent'}
                    {!s.agent_online && s.agent_name ? ' (offline)' : ''}
                  </div>
                </div>
                <div className="row mono" style={{ gap: 10, fontSize: 13 }}>
                  <span style={{ color: 'var(--ok)' }}>{s.devices_ok}</span>
                  <span style={{ color: 'var(--warn)' }}>{s.devices_warn}</span>
                  <span style={{ color: 'var(--crit)' }}>{s.devices_down}</span>
                </div>
              </div>
            ))
          )}
        </section>
      </div>

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Letzte Config-Änderungen</h2>
          <Link to={pagePath('configs', customerSlug)} style={{ fontSize: 13 }}>
            Alle Backups
          </Link>
        </div>
        {data.recent_config_changes.length === 0 ? (
          <div className="empty">
            <span>Keine Konfigurationsänderung in den gesicherten Ständen.</span>
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Gerät</th>
                <th>Kunde / Standort</th>
                <th>Umfang</th>
                <th>Zeitpunkt</th>
                <th>Prüfung</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_config_changes.map((change) => (
                <tr key={change.backup_id}>
                  <td>
                    <Link
                      to={`${pagePath('geraete', change.customer_slug || customerSlug)}/${change.device_id}`}
                      className="mono"
                    >
                      {change.device_name}
                    </Link>
                  </td>
                  <td className="muted">
                    {change.customer_name} · {change.site_name}
                  </td>
                  <td className="mono">
                    +{change.added} −{change.removed}
                  </td>
                  <td className="muted">{formatDateTime(change.taken_at)}</td>
                  <td>
                    {change.review_state === 'offen' ? (
                      <StatusBadge state="warn" label="ungeprüft" />
                    ) : change.review_state === 'beanstandet' ? (
                      <StatusBadge state="crit" label="beanstandet" />
                    ) : (
                      <StatusBadge state="ok" label="geprüft" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  )
}

function Kpi({
  label,
  value,
  sub,
  tone,
}: {
  label: string
  value: number
  sub: string
  tone: 'ok' | 'warn' | 'crit' | 'off'
}) {
  const colors = {
    ok: 'var(--ok)',
    warn: 'var(--warn)',
    crit: 'var(--crit)',
    off: 'var(--muted)',
  }
  return (
    <div className="kpi">
      <span className="kpi-label">{label}</span>
      <span className="kpi-value" style={{ color: colors[tone] }}>
        {value}
      </span>
      <span className="kpi-sub">{sub}</span>
    </div>
  )
}
